import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AsyncActivitiesUser,
  AsynchronousActivityRequest,
  AttendanceModel,
  AttendanceRequest,
  AttendanceScheduleRequest,
  LeaderSlotSuggestionModel,
  ReassignLeaderRequest,
  ReassignWeekAbsentRequest,
} from '@tsin-core/models/attendance.model';
import { environment } from '@tsin-env/environment';
import { Observable } from 'rxjs';

const ATTENDANCE_URL = `${environment.learningBaseUrl}/attendance`;
const ASYNC_ACTIVITY_URL = `${environment.learningBaseUrl}/async-activities`;

@Injectable({ providedIn: 'root' })
export class AttendanceService {
  http: HttpClient = inject(HttpClient);

  updateAttendanceStatus(payload: AttendanceRequest) {
    return this.http.post(ATTENDANCE_URL, payload);
  }

  updateAsynchronousActivity(payload: AsynchronousActivityRequest): Observable<AttendanceModel> {
    return this.http.post<AttendanceModel>(`${ASYNC_ACTIVITY_URL}`, payload);
  }

  getAttendanceBySchedule(payload: AttendanceScheduleRequest): Observable<AttendanceModel[]> {
    return this.http.get<AttendanceModel[]>(`${ATTENDANCE_URL}/schedule/${payload.weeklyScheduleId}/${payload.sessionId}`);
  }

  getAttendanceByWeeklySchedule(weeklyScheduleId: string): Observable<AttendanceModel[]> {
    return this.http.get<AttendanceModel[]>(`${ATTENDANCE_URL}/schedule/${weeklyScheduleId}`);
  }

  getAsynchronousActivityByWeekly(weeklyScheduleId: string): Observable<AsyncActivitiesUser[]> {
    return this.http.get<AsyncActivitiesUser[]>(`${ASYNC_ACTIVITY_URL}/eligible-users/weeklyId/${weeklyScheduleId}`);
  }

  // ─── Leader Replacement API calls ────────────────────────────────────────

  /**
   * Fetch scored replacement candidates for all leadership slots held by
   * the given session-user.
   * Récupérer les candidats de remplacement classés pour tous les créneaux
   * de leadership de l'utilisateur de session.
   */
  getLeaderSuggestion(sessionUserId: string): Observable<LeaderSlotSuggestionModel[]> {
    return this.http.get<LeaderSlotSuggestionModel[]>(`${ATTENDANCE_URL}/leader-suggestion/${sessionUserId}`);
  }

  /**
   * Manually reassign a single leader slot.
   * Réassigner manuellement un créneau de leader.
   */
  reassignLeader(payload: ReassignLeaderRequest): Observable<any> {
    return this.http.post(`${ATTENDANCE_URL}/reassign-leader`, payload);
  }

  /**
   * Auto-reassign all leadership slots for a week-absent learner.
   * Réassigner automatiquement tous les créneaux d'un apprenant absent.
   */
  reassignWeekAbsent(payload: ReassignWeekAbsentRequest): Observable<{ reassigned: string[]; conflicts: string[]; conflictsFr: string[] }> {
    return this.http.post<{ reassigned: string[]; conflicts: string[]; conflictsFr: string[] }>(`${ATTENDANCE_URL}/reassign-week-absent`, payload);
  }
}
